import * as THREE from 'three';

/**
 * A subtle glow rendered on a sphere slightly larger than the globe, with
 * `BackSide` material and additive blending. The fragment shader uses a
 * fresnel-style falloff so the glow is strongest at the limb (edge) of the
 * globe and fades toward the center — giving the "atmosphere" silhouette.
 *
 * Colored with the Atlas accent gold to keep it on-brand even in the
 * outline display mode (when no photo texture is present).
 */
const VERTEX_SHADER = /* glsl */ `
  varying vec3 vNormal;
  void main() {
    vNormal = normalize(normalMatrix * normal);
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FRAGMENT_SHADER = /* glsl */ `
  uniform vec3 glowColor;
  uniform float intensity;
  varying vec3 vNormal;
  void main() {
    // Power curve controls how tight/diffuse the glow is — higher = tighter
    // ring, lower = softer, wider bloom. 2.0 gives a gentle haze rather than
    // a crisp rim, which reads more like Earth's atmosphere from orbit.
    float strength = pow(1.0 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
    gl_FragColor = vec4(glowColor, 1.0) * strength * intensity;
  }
`;

export interface AtmosphereOptions {
  /** Globe surface radius — the atmosphere renders on a slightly larger sphere. */
  globeRadius: number;
  /** Glow color as a THREE.Color or hex number. Defaults to pale sky-blue. */
  color?: THREE.ColorRepresentation;
  /** Overall brightness multiplier (0–1). Defaults to 0.4 — subtle haze. */
  intensity?: number;
  /** How much larger the atmosphere sphere is relative to the globe. Defaults to 1.05 (wider spread). */
  scale?: number;
}

/**
 * Build the atmosphere mesh. Caller is responsible for adding it to the scene
 * and disposing its geometry/material when done.
 *
 * Default color is a cool pale-blue — reads as "Earth's atmosphere" against
 * both the outline-mode dark sphere and the photorealistic NASA texture.
 * Brand-gold accents live in UI chrome (HUD, markers, dialogs), not on the
 * globe itself.
 */
export function createAtmosphere(options: AtmosphereOptions): THREE.Mesh {
  const { globeRadius, color = 0x93c5e8, intensity = 0.4, scale = 1.05 } = options;

  const geometry = new THREE.SphereGeometry(globeRadius * scale, 64, 64);

  const material = new THREE.ShaderMaterial({
    uniforms: {
      glowColor: { value: new THREE.Color(color) },
      intensity: { value: intensity },
    },
    vertexShader: VERTEX_SHADER,
    fragmentShader: FRAGMENT_SHADER,
    side: THREE.BackSide,
    blending: THREE.AdditiveBlending,
    transparent: true,
    depthWrite: false,
  });

  const mesh = new THREE.Mesh(geometry, material);
  mesh.name = 'atmosphere';
  return mesh;
}
